'use es6';

import { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { ScrollingColumn } from 'foundations-components/transitional/components/ScrollingColumn';
import { Table } from 'foundations-components/transitional/components/Table';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { Select } from 'foundations-components/transitional/components/Select';
import { Flex } from 'foundations-components/components/Flex';
import { Spacer } from 'foundations-components/components/Spacer';
import WidgetIcon from 'ContentComponents/components/cmv2/WidgetIcon';
import { H2 } from 'foundations-components/transitional/components/H2';
import { getAllSortedAddableModuleSchemasForThisPagesTheme, getAddableCommonModules, getAlphabeticallySortedBuiltInModuleSchemas, getAddableThemeModules, getAddableCustomModuleSchemasForThisPagesTheme, getGlobalModulesForThisPagesTheme } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import { getHasTheme } from 'ContentEditorUI/redux/selectors/themeSelectors';
import { getIsAdvancedPageTemplateWithAccess } from 'ContentEditorUI/redux/selectors/templateInfoSelectors';
import { shouldShowSchema } from 'ContentEditorUI/lib/moduleSearchHelpers';
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ModuleSelectorTableRow = styled.tr.withConfig({
  displayName: "ModuleSelectorModal__ModuleSelectorTableRow",
  componentId: "sc-112hp2f-0"
})(({
  theme,
  $isActive
}) => {
  const activeStyles = $isActive ? {
    backgroundColor: theme.color['fill-positive-subtle']
  } : {};
  return Object.assign({
    cursor: 'pointer'
  }, activeStyles);
});
const mapStateToProps = state => {
  const hasTheme = getHasTheme(state);
  return {
    allModuleCollection: getAllSortedAddableModuleSchemasForThisPagesTheme(state),
    defaultModuleCollection: getAlphabeticallySortedBuiltInModuleSchemas(state),
    globalModuleCollection: getGlobalModulesForThisPagesTheme(state),
    customModuleCollection: getAddableCustomModuleSchemasForThisPagesTheme(state),
    commonModuleCollection: getAddableCommonModules(state),
    themeModuleCollection: getAddableThemeModules(state),
    hasTheme,
    isAdvancedPageTemplateWithAccess: getIsAdvancedPageTemplateWithAccess(state)
  };
};
class ModuleSelectorModal extends Component {
  constructor(props) {
    super(props);
    this.closeDialog = () => {
      this.props.closeModalCallback();
    };
    this.handleRowClick = e => {
      this.setState({
        selectedModuleId: e.currentTarget.dataset.moduleId
      });
    };
    this.handleSearchKeyup = e => {
      this.setState({
        query: e.target.value
      });
    };
    this.handleTypeChange = e => {
      this.setState({
        typeFilter: e.target.value
      });
    };
    this.renderTableRow = widget => {
      const id = (widget.id || widget.name).toString();
      return (
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx(ModuleSelectorTableRow, {
          $isActive: this.checkIsActive(widget),
          children: /*#__PURE__*/_jsxs("td", {
            onClick: this.handleRowClick,
            "data-module-id": id,
            "data-module-type": widget.content_widget_type,
            "data-selenium": widget.name,
            children: [this.renderIcon(widget), widget.displayName]
          })
        }, id)
      );
    };
    this.state = {
      selectedModuleId: null,
      typeFilter: 'all',
      query: ''
    };
  }
  langKey(suffix) {
    return `moduleSelector.${suffix}`;
  }
  submitHandler(callback) {
    callback(this.props.containerId, this.state.selectedModuleId);
    this.closeDialog();
  }
  checkIsActive(widget) {
    const {
      selectedModuleId
    } = this.state;
    return selectedModuleId === (widget.id || widget.name).toString();
  }
  getTypeOptions() {
    const {
      hasTheme,
      isAdvancedPageTemplateWithAccess
    } = this.props;
    const options = [{
      value: 'all',
      text: I18n.text(this.langKey('typeFilter.all'))
    }, {
      value: 'default',
      text: I18n.text(this.langKey('typeFilter.default'))
    }, {
      value: 'global',
      text: I18n.text(this.langKey('typeFilter.global'))
    }, {
      value: 'custom',
      text: I18n.text(this.langKey('typeFilter.custom'))
    }];

    // Only show "common" modules for starter templates
    if (!isAdvancedPageTemplateWithAccess) {
      return [{
        value: 'common',
        text: I18n.text(this.langKey('typeFilter.all'))
      }];
    }
    if (hasTheme) {
      options.push({
        value: 'theme',
        text: I18n.text(this.langKey('typeFilter.theme'))
      });
    }
    return options;
  }
  getCollectionForTypeSelected() {
    const {
      typeFilter
    } = this.state;
    switch (typeFilter) {
      case 'all':
        return this.props.allModuleCollection;
      case 'common':
        return this.props.commonModuleCollection;
      case 'default':
        return this.props.defaultModuleCollection;
      case 'global':
        return this.props.globalModuleCollection;
      case 'custom':
        return this.props.customModuleCollection;
      case 'theme':
        return this.props.themeModuleCollection;
      default:
        throw new Error(`Unknown type filter: ${typeFilter}`);
    }
  }
  getCollectionForTypeSelectedAndFilter() {
    const {
      query
    } = this.state;
    return this.getCollectionForTypeSelected().filter(module => {
      return shouldShowSchema(module, query.toLowerCase());
    });
  }
  renderIcon(widget) {
    var _widget$icon;
    return /*#__PURE__*/_jsx(WidgetIcon, {
      marginInlineEnd: "200",
      icon: (_widget$icon = widget.icon) !== null && _widget$icon !== void 0 ? _widget$icon : ''
    });
  }
  render() {
    const moduleList = this.getCollectionForTypeSelectedAndFilter();
    return /*#__PURE__*/_jsxs(Modal, {
      width: 600,
      "data-test-id": "add-module-modal",
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: this.closeDialog
        }), /*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: this.langKey('header')
          })
        })]
      }), /*#__PURE__*/_jsx(DialogBody, {
        children: /*#__PURE__*/_jsx(ScrollingColumn, {
          maxHeight: 400,
          headerContent: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(Flex, {
              gap: "400",
              children: [/*#__PURE__*/_jsx(Select, {
                anchorType: "button",
                onChange: this.handleTypeChange,
                defaultValue: "all",
                options: this.getTypeOptions()
              }), /*#__PURE__*/_jsx(SearchInput, {
                placeholder: I18n.text(this.langKey('searchPlaceholder')),
                onKeyUp: this.handleSearchKeyup
              })]
            }), /*#__PURE__*/_jsx(Spacer, {
              size: "400"
            })]
          }),
          children: /*#__PURE__*/_jsx(Table, {
            condensed: true,
            children: /*#__PURE__*/_jsx("tbody", {
              children: moduleList.map(this.renderTableRow)
            })
          })
        })
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(Button, {
          use: "primary",
          "data-test-id": "add-module",
          onClick: this.submitHandler.bind(this, this.props.moduleSelectCallback, true),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: this.langKey('addModule')
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "secondary",
          onClick: this.closeDialog,
          "data-test-id": "close-add-module-modal",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: this.langKey('cancel')
          })
        })]
      })]
    });
  }
}
ModuleSelectorModal.propTypes = {
  containerId: PropTypes.string.isRequired,
  allModuleCollection: PropTypes.array.isRequired,
  defaultModuleCollection: PropTypes.array.isRequired,
  globalModuleCollection: PropTypes.array.isRequired,
  customModuleCollection: PropTypes.array.isRequired,
  commonModuleCollection: PropTypes.array.isRequired,
  themeModuleCollection: PropTypes.array.isRequired,
  hasTheme: PropTypes.bool.isRequired,
  isAdvancedPageTemplateWithAccess: PropTypes.bool.isRequired,
  moduleSelectCallback: PropTypes.func.isRequired,
  closeModalCallback: PropTypes.func.isRequired
};
export default connect(mapStateToProps)(ModuleSelectorModal);